/**
 * Toon dungeon builder. Picks the dungeon's theme (themes/index.ts) and builds its static
 * dressing plus doors:
 *  - legacy themes on flat dungeons: the original KayKit dressing (themes/generic.ts)
 *  - everything else: the generic themed builder below, from pure layouts
 *      dressing/arch.ts   floors (per-area patterns), stair flights, raised ledges, walls, posts
 *      dressing/dress.ts  hero props, ledge clutter, wall decor, foliage, lights, decals, shafts
 *    meshed per room cluster and per material (merged, culled per cluster):
 *      floors (one per floor pattern), stone (ledges, walls, cliffs, risers), trim (serpent
 *      frieze on ledge lips and far walls), props (all kit + procedural props on one atlas, one
 *      draw + one outline hull) and detail props (small clutter, hidden on the low preset)
 *    plus a handful of global draws: flames, light pools, water, shafts, floor decals, mosaics.
 *    Enclosed themes (theme.arch.enclosed, +dungeon2) add full-height walls, arched lintels,
 *    vaults / timber roofs / arcade roofs and beams on cutaway materials (cutaway.ts).
 *  - doors (doors.ts) for any theme, animated from door_open / door_close events.
 * Floors stay mid-dark and non-emissive; light is faked with vertex colour and glow cards.
 */
import * as THREE from 'three';
import type { DungeonDef, SimEvent, Vec2 } from '@keystone/sim';
import { ATLAS_WHITE, type ToonAssets } from './assets';
import type { ArchLayout, FloorPiece, LedgePiece } from './dressing/arch';
import { type Blocker, blockersFromArch, blockersFromWalkable } from './camCollision'; // +camera
import { DECAL_KINDS, type DressLayout, type Placement } from './dressing/dress';
import { planDungeon } from './dressing/plan';
import { type Poly, type Pt, bbox, clipConvex, distToSegment, fitPlane, offsetConvex, planeAt, pointInConvex, rectPoly, subtractAll } from './dressing/geom2d';
import { DoorAnimator, type DoorVisual, buildDoorVisuals } from './doors';
import { DECAL_CELL, floorDecalTexture, lightPoolMaterial, poisonWaterMaterial, shaftMaterial } from './envMaterials';
import { dungeonHasElevation } from './ground';
import { computeWallLayout } from './layout';
import { type HullRegistry, NOISE_GLSL, U, addHull, addOutlineNormals, flameMaterial, ownedToonMaterial, toonColor } from './style';
import { FLOOR_BASE, buildLegacyDungeon, makeFlagstoneTexture } from './themes/generic';
import { themeFor } from './themes/index';
import type { DungeonTheme, RGB, SurfaceStyle } from './themes/types';
import { venomProc } from './themes/venomAltar/procs';
import { CUT_KIND, cutawayMaterial, resetCutaway, updateCutaway } from './cutaway'; // +dungeon2
import { enclosedBlockers } from './dressing/enclosed'; // +dungeon2
import { buildPatrolOverlay, patrolsRequested } from './patrolOverlay'; // +patrols
import { buildToonField } from './outdoor/field'; // +field
import { outdoorThemeFor } from './outdoor/themes'; // +field
import { outdoorProc } from './outdoor/procs'; // +field
import { groundGlowMaterial } from './outdoor/glow'; // +field

/** +dungeon2: aCut for geometry the cutaway never removes (section caps). */
const CUT_NEVER = [0, 0, 0, CUT_KIND.never] as const;

export { FLOOR_BASE, makeFlagstoneTexture };

export interface DungeonBuild {
  group: THREE.Group;
  /** First floor material (compat); use setFloorBrightness for all of them. */
  floorMaterial: THREE.MeshToonMaterial;
  theme: DungeonTheme;
  /** +camera: the themed arch layout (null for the legacy KayKit builder). */
  arch: ArchLayout | null;
  /**
   * +camera: vertical blockers for camera collision (camCollision.ts `Blocker`): walls, posts,
   * tall props, arches. New dressing registers its solid geometry here; ceilings / upper walls the
   * camera cutaway hides are left out. Read once per dungeon init.
   */
  cameraBlockers(): readonly Blocker[];
  /**
   * +dungeon2: point the enclosed-interior cutaway (cutaway.ts) at this frame's camera (three.js
   * position) and the party focus (sim x/y + floor height). No-op for open dungeons.
   */
  cutaway(cam: THREE.Vector3, focus: Vec2, groundY: number): void;
  counts: { walls: number; props: number; torches: number; drawables: number; doors: number; clusters: number };
  /** Apply the floor-brightness setting to every floor material (keeps each pattern's base). */
  setFloorBrightness(v: number): void;
  /** Small clutter + foliage on/off (the low preset hides it). */
  setDetail(on: boolean): void;
  /** Door events (door_open / door_close / encounter_end) and animation. */
  ingest(events: readonly SimEvent[] | undefined): void;
  update(dtS: number): void;
  /** Snap doors to their initial state (restart). */
  resetDoors(): void;
  dispose(): void;
}

export function buildToonDungeon(d: DungeonDef, assets: ToonAssets, hulls: HullRegistry, maxAniso: number): DungeonBuild {
  // +field: open fields and the town have their own outdoor builder (outdoor/field.ts).
  const outdoor = outdoorThemeFor(d);
  if (outdoor) return buildToonField(d, outdoor, assets, hulls, maxAniso);
  const theme = themeFor(d);
  const legacy = theme.legacy && !dungeonHasElevation(d);
  const owned: { dispose(): void }[] = [];
  let core: { group: THREE.Group; floorMaterials: THREE.MeshToonMaterial[]; detail: THREE.Object3D[]; counts: DungeonBuild['counts']; dispose(): void; arch: ArchLayout | null };
  if (legacy) {
    const b = buildLegacyDungeon(d, assets, hulls, maxAniso);
    core = { group: b.group, floorMaterials: [b.floorMaterial], detail: [], counts: { ...b.counts, doors: 0, clusters: 1 }, dispose: b.dispose, arch: null };
  } else {
    core = buildThemed(d, theme, assets, hulls, maxAniso);
  }

  // ---- doors (any theme) ---------------------------------------------------------------
  const defs = d.doors ?? [];
  const animator = new DoorAnimator(defs);
  let visuals: DoorVisual[] = [];
  if (defs.length) {
    const tex = (s: SurfaceStyle): THREE.Texture | null => (s.texture && !s.texture.startsWith('canvas:') ? assets.texture(s.texture) : null);
    const woodMap = tex(theme.surfaces.wood);
    const stoneMap = tex(theme.surfaces.stone);
    const faceMap = assets.texture('venom_decal_serpent_face');
    const wood = ownedToonMaterial({ color: woodMap ? 0xffffff : theme.surfaces.wood.tint, map: woodMap });
    const stone = ownedToonMaterial({ color: stoneMap ? 0xe8dcc4 : theme.surfaces.stone.tint, map: stoneMap });
    const face = faceMap && theme.id === 'venom_altar' ? ownedToonMaterial({ map: faceMap, transparent: false, alphaTest: 0.5, color: 0xf2eadc, side: THREE.FrontSide }) : null;
    const vines = ownedToonMaterial({ vertexColors: true, color: 0xffffff });
    owned.push(wood, stone, vines, ...(face ? [face] : []));
    const walk = (x: number, y: number): boolean => (core.arch ? core.arch.inWalkable({ x, y }) : d.rooms.some((r) => pointInPolyQuick({ x, y }, r.shape.points)) || d.corridors.some((c) => pointInPolyQuick({ x, y }, c.shape.points)));
    const groundAt = (x: number, y: number): number => (core.arch ? core.arch.floorAt({ x, y }) : 0);
    visuals = buildDoorVisuals(defs, { groundAt, walkable: walk, mats: { wood, stone, face, iron: toonColor(0x3a3a40), vines }, hulls, owned }, U.uTime);
    for (const v of visuals) {
      v.pose(animator.progress(v.def.id), false);
      core.group.add(v.group);
    }
  }
  const poseAll = (): void => {
    for (const v of visuals) {
      const s = animator.states.get(v.def.id)!;
      v.pose(s.p, s.closed);
      if (v.glow) {
        v.glow.visible = s.p > 0.01;
        ((v.glow.material as THREE.ShaderMaterial).uniforms['uK'] as { value: number }).value = 0.4 * s.p;
      }
    }
  };
  poseAll();

  // +patrols: ?debug=patrols draws patrol paths, wander circles and loose/linked packs.
  if (typeof location !== 'undefined' && patrolsRequested(location.search)) core.group.add(buildPatrolOverlay(d, (p) => (core.arch ? core.arch.floorAt(p) : 0), owned));

  const counts = { ...core.counts, doors: visuals.length };
  return {
    group: core.group,
    floorMaterial: core.floorMaterials[0]!,
    theme,
    arch: core.arch, // +camera
    cameraBlockers: () => (core.arch?.enclosed ? enclosedBlockers(core.arch) : core.arch ? blockersFromArch(core.arch) : blockersFromWalkable([...d.rooms.map((r) => r.shape), ...d.corridors.map((c) => c.shape)])), // +camera (+dungeon2: enclosed walls block up to their cut height)
    cutaway(cam, focus, groundY) {
      if (core.arch?.enclosed) updateCutaway(cam, focus.x, groundY, -focus.y); // +dungeon2
    },
    counts,
    setFloorBrightness(v: number) {
      for (const m of core.floorMaterials) {
        const base = (m.userData['base'] as number | undefined) ?? FLOOR_BASE;
        const tint = m.userData['tint'] as THREE.Color | undefined;
        m.color.setScalar(v * base);
        if (tint) m.color.multiply(tint);
      }
    },
    setDetail(on: boolean) {
      for (const o of core.detail) o.visible = on;
    },
    ingest(events) {
      if (animator.ingest(events)) poseAll();
    },
    update(dtS: number) {
      if (animator.update(dtS)) poseAll();
    },
    resetDoors() {
      animator.reset();
      poseAll();
    },
    dispose() {
      core.dispose();
      for (const o of owned) o.dispose();
      resetCutaway(); // +dungeon2
    },
  };
}

function pointInPolyQuick(p: Vec2, pts: readonly Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

// =========================================================================================
// Geometry buckets
// =========================================================================================

class Bucket {
  p: number[] = [];
  n: number[] = [];
  uv: number[] = [];
  c: number[] = [];
  /** +dungeon2: cutaway attribute (cutaway.ts `aCut`) per vertex, when the bucket's material cuts. */
  k: number[] = [];
  cutOn = false;
  cut: readonly [number, number, number, number] = [0, 0, 0, 0];
  get count(): number {
    return this.p.length / 3;
  }
  v(x: number, y: number, z: number, nx: number, ny: number, nz: number, u: number, w: number, r: number, g: number, b: number): void {
    this.p.push(x, y, z);
    this.n.push(nx, ny, nz);
    this.uv.push(u, w);
    this.c.push(r, g, b);
    if (this.cutOn) this.k.push(this.cut[0], this.cut[1], this.cut[2], this.cut[3]);
  }
  geometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.p, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.n, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.c, 3));
    if (this.cutOn) g.setAttribute('aCut', new THREE.Float32BufferAttribute(this.k, 4));
    g.computeBoundingSphere();
    return g;
  }
}

const lin = (hex: number): RGB => {
  const c = new THREE.Color(hex);
  return [c.r, c.g, c.b];
};
const mul = (a: RGB, k: number | RGB): RGB => (typeof k === 'number' ? [a[0] * k, a[1] * k, a[2] * k] : [a[0] * k[0], a[1] * k[1], a[2] * k[2]]);
const smooth = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** Value noise for vertex variation (deterministic). */
function hash2(x: number, y: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function vnoise(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi);
  const b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1);
  const e = hash2(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + e) * u * v;
}

/** Uniform grid over 2D segments / points for fast "nearest" queries during the bake. */
class SegGrid {
  private cells = new Map<number, number[]>();
  constructor(
    private readonly segs: readonly [Pt, Pt][],
    private readonly cell = 6,
  ) {
    segs.forEach(([a, b], i) => {
      const x0 = Math.floor(Math.min(a.x, b.x) / cell);
      const x1 = Math.floor(Math.max(a.x, b.x) / cell);
      const y0 = Math.floor(Math.min(a.y, b.y) / cell);
      const y1 = Math.floor(Math.max(a.y, b.y) / cell);
      for (let x = x0; x <= x1; x++)
        for (let y = y0; y <= y1; y++) {
          const k = x * 4096 + y;
          const l = this.cells.get(k);
          if (l) l.push(i);
          else this.cells.set(k, [i]);
        }
    });
  }
  /** Distance to the nearest segment, capped at `max`. */
  dist(p: Pt, max: number): number {
    let best = max;
    const r = Math.ceil(max / this.cell);
    const cx = Math.floor(p.x / this.cell);
    const cy = Math.floor(p.y / this.cell);
    for (let x = cx - r; x <= cx + r; x++)
      for (let y = cy - r; y <= cy + r; y++) {
        const l = this.cells.get(x * 4096 + y);
        if (!l) continue;
        for (const i of l) {
          const [a, b] = this.segs[i]!;
          const d = distToSegment(p, a, b);
          if (d < best) best = d;
        }
      }
    return best;
  }
}

// =========================================================================================
// Themed builder
// =========================================================================================

function buildThemed(d: DungeonDef, theme: DungeonTheme, assets: ToonAssets, hulls: HullRegistry, maxAniso: number) {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  const group = new THREE.Group();
  group.name = 'toon-dungeon';
  const owned: { dispose(): void }[] = [];
  const detail: THREE.Object3D[] = [];
  let drawables = 0;

  const { arch, dress } = planDungeon(d, theme);

  // ---- textures / materials -----------------------------------------------------------
  const S0 = (): DungeonTheme['surfaces'] => theme.surfaces;
  let canvasFlag: THREE.Texture | null = null;
  const texFor = (s: SurfaceStyle): THREE.Texture | null => {
    if (!s.texture) return null;
    if (s.texture === 'canvas:flagstone') {
      if (!canvasFlag && typeof document !== 'undefined') {
        canvasFlag = makeFlagstoneTexture(maxAniso);
        canvasFlag.repeat.set(1, 1);
        owned.push(canvasFlag);
      }
      return canvasFlag;
    }
    const t = assets.texture(s.texture);
    if (t) t.anisotropy = Math.min(8, maxAniso);
    return t;
  };
  const floorMats = new Map<string, THREE.MeshToonMaterial>();
  const floorMaterial = (kind: string, path = false): THREE.MeshToonMaterial => {
    const key = path ? `path:${kind}` : kind;
    let m = floorMats.get(key);
    if (m) return m;
    const st = theme.floors[kind] ?? Object.values(theme.floors)[0]!;
    const map = texFor(st);
    m = ownedToonMaterial({ color: 0xffffff, map, vertexColors: true, ...(path ? { polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2 } : {}) });
    m.userData['base'] = FLOOR_BASE * (st.base ?? 1);
    m.userData['tint'] = new THREE.Color(st.tint);
    m.color.setScalar(m.userData['base'] as number).multiply(m.userData['tint'] as THREE.Color);
    m.name = `floor:${key}`;
    antiTile(m);
    owned.push(m);
    floorMats.set(key, m);
    return m;
  };
  const ledgeMat = ownedToonMaterial({ color: 0xffffff, map: texFor(S0().ledgeTop), vertexColors: true });
  ledgeMat.name = 'ledge';
  antiTile(ledgeMat);
  owned.push(ledgeMat);
  const S = S0();
  const stoneMat = ownedToonMaterial({ color: 0xffffff, map: texFor(S.stone), vertexColors: true });
  stoneMat.name = 'stone';
  const trimMat = ownedToonMaterial({ color: 0xffffff, map: texFor(S.trim), vertexColors: true });
  trimMat.name = 'trim';
  owned.push(stoneMat, trimMat);
  const stoneTile = S.stone.tileYd;
  const trimTile = S.trim.tileYd;
  const ledgeTint = lin(S.ledgeTop.tint);
  const cliffTint = lin(S.cliff.tint);
  const wallTint = lin(S.stone.tint);
  const mossTint: RGB = [0.62, 0.74, 0.46];
  const trimOn = theme.arch.trim && !!S.trim.texture;

  // ---- buckets per (cluster, material) --------------------------------------------------
  type BucketKind = 'floor' | 'stone' | 'trim' | 'ledge' | 'path' | 'wall' | 'ceil' | 'beam' | 'roof';
  const buckets = new Map<string, { b: Bucket; mat: THREE.Material; cluster: number; kind: BucketKind }>();
  const bucket = (cluster: number, kind: BucketKind, mat: THREE.Material): Bucket => {
    const key = `${cluster}|${kind}|${mat.uuid}`;
    let e = buckets.get(key);
    if (!e) buckets.set(key, (e = { b: new Bucket(), mat, cluster, kind }));
    return e.b;
  };
  // +dungeon2: enclosed interiors. Walls, lintels, posts, beams, roofs and ceilings use cutaway
  // materials (cutaway.ts) and carry a per-vertex cut (outward normal, cut height, kind).
  const enclosed = arch.enclosed;
  const wallCutMat = cutawayMaterial({ color: 0xffffff, map: texFor(S0().stone), vertexColors: true });
  wallCutMat.name = 'wall-cut';
  const trimCutMat = cutawayMaterial({ color: 0xffffff, map: texFor(S0().trim), vertexColors: true });
  trimCutMat.name = 'trim-cut';
  const woodCutMat = cutawayMaterial({ color: 0xffffff, map: texFor(S0().wood), vertexColors: true });
  woodCutMat.name = 'wood-cut';
  owned.push(wallCutMat, trimCutMat, woodCutMat);
  const cutBucket = (cluster: number, kind: BucketKind, mat: THREE.Material, cut: readonly [number, number, number, number]): Bucket => {
    const b = bucket(cluster, kind, mat);
    b.cutOn = true;
    b.cut = cut;
    return b;
  };

  // ---- bake inputs: ledge lips (AO), lights (warm / jade), pools (wet) ---------------------
  const lip = computeWallLayout(arch.walkable.map((p) => ({ points: p })), 1, 0.4);
  const lipGrid = new SegGrid(lip.walls.map((w) => [w.a, w.b] as [Pt, Pt]));
  const glowWarm = theme.glow.warm;
  const glowJade = theme.glow.jade;
  const lights = dress.glows.map((g) => ({ pos: g.pos, r: g.r * 1.5, k: g.strength, jade: g.temp === 'jade' }));
  const lightGrid = new Map<number, number[]>();
  lights.forEach((l, i) => {
    for (let x = Math.floor((l.pos.x - l.r) / 8); x <= Math.floor((l.pos.x + l.r) / 8); x++)
      for (let y = Math.floor((l.pos.y - l.r) / 8); y <= Math.floor((l.pos.y + l.r) / 8); y++) {
        const k = x * 4096 + y;
        lightGrid.set(k, [...(lightGrid.get(k) ?? []), i]);
      }
  });
  const roomLight = new Map(arch.rooms.map((r) => [r.id, r.style.light ?? 'neutral']));
  const bakeFloor = (x: number, y: number, owner: string): RGB => {
    const p = { x, y };
    const dEdge = lipGrid.dist(p, 3);
    let r = 1;
    let g = 1;
    let b = 1;
    // Painted AO along ledge lips and walls.
    const ao = 1 - 0.3 * (1 - smooth(0, 2.4, dEdge));
    // Broad value variation + moss creeping in from the edges.
    const v = 0.93 + 0.12 * vnoise(x * 0.09, y * 0.09);
    const m = smooth(0.58, 0.85, vnoise(x * 0.17 + 40, y * 0.17 - 12)) * (0.35 + 0.65 * (1 - smooth(0, 7, dEdge))) * 0.55;
    r *= 1 + (mossTint[0] - 1) * m;
    g *= 1 + (mossTint[1] - 1) * m;
    b *= 1 + (mossTint[2] - 1) * m;
    // Baked light pools.
    for (const i of lightGrid.get(Math.floor(x / 8) * 4096 + Math.floor(y / 8)) ?? []) {
      const l = lights[i]!;
      const dd = Math.hypot(l.pos.x - x, l.pos.y - y);
      if (dd >= l.r) continue;
      const k = (1 - dd / l.r) ** 2 * l.k;
      if (l.jade) {
        r *= 1 - 0.1 * k;
        g *= 1 + 0.12 * k;
        b *= 1 + 0.08 * k;
      } else {
        r *= 1 + 0.2 * k;
        g *= 1 + 0.08 * k;
        b *= 1 - 0.08 * k;
      }
    }
    // Cool rooms get a whisper of jade bounce overall.
    if (roomLight.get(owner) === 'jade') {
      r *= 0.96;
      b *= 1.02;
    }
    const k = ao * v;
    return [Math.min(1.15, r * k), Math.min(1.12, g * k), Math.min(1.08, b * k)];
  };

  // ---- emit helpers ----------------------------------------------------------------------
  const CELL = 2.5;
  /** Flat / planar polygon top, subdivided on a grid so vertex colours can carry the bake. */
  const emitTop = (bk: Bucket, poly: Pt[], h: number[], tile: number, color: (x: number, y: number) => RGB, cell = CELL): void => {
    const pl = fitPlane(poly, h);
    const bb = bbox(poly);
    const pieces: Pt[][] = [];
    const span = Math.max(bb.x1 - bb.x0, bb.y1 - bb.y0);
    if (span <= cell * 1.2) pieces.push(poly);
    else
      for (let x = Math.floor(bb.x0 / cell) * cell; x < bb.x1; x += cell)
        for (let y = Math.floor(bb.y0 / cell) * cell; y < bb.y1; y += cell) {
          const c = clipConvex(rectPoly(x, y, x + cell, y + cell), poly);
          if (c.length >= 3) pieces.push(c);
        }
    // Normal of the plane (sim slope → three normal).
    const nx = -pl.a;
    const nz = pl.b;
    const nl = Math.hypot(nx, 1, nz);
    for (const q of pieces) {
      const cols = q.map((p) => color(p.x, p.y));
      for (let i = 1; i < q.length - 1; i++) {
        for (const k of [0, i, i + 1]) {
          const p = q[k]!;
          const c = cols[k]!;
          bk.v(p.x, planeAt(pl, p.x, p.y), -p.y, nx / nl, 1 / nl, nz / nl, p.x / tile, p.y / tile, c[0], c[1], c[2]);
        }
      }
    }
  };
  /** Vertical quad p→q (outward normal to the right of p→q), heights per end. */
  const emitSide = (bk: Bucket, p: Pt, q: Pt, y0p: number, y1p: number, y0q: number, y1q: number, tile: number, col: RGB, colTop?: RGB, vBand?: number): void => {
    const len = Math.hypot(q.x - p.x, q.y - p.y);
    if (len < 1e-4 || (y1p - y0p < 1e-3 && y1q - y0q < 1e-3)) return;
    const dx = (q.x - p.x) / len;
    const dy = (q.y - p.y) / len;
    const nx = dy;
    const nz = dx; // three z = -sim y; outward sim (dy, -dx) → three (dy, 0, dx)
    const u0 = (p.x * dx + p.y * dy) / tile;
    const u1 = u0 + len / tile;
    const v = (y: number, base: number, top: number): number => (vBand !== undefined ? (vBand * (y - base)) / Math.max(1e-3, top - base) : y / tile);
    const ct = colTop ?? col;
    const A = [p.x, y0p, -p.y, u0, v(y0p, y0p, y1p), col] as const;
    const B = [q.x, y0q, -q.y, u1, v(y0q, y0q, y1q), col] as const;
    const C = [q.x, y1q, -q.y, u1, v(y1q, y0q, y1q), ct] as const;
    const D = [p.x, y1p, -p.y, u0, v(y1p, y0p, y1p), ct] as const;
    for (const V of [A, B, C, A, C, D]) bk.v(V[0], V[1], V[2], nx, 0, nz, V[3], V[4], V[5][0], V[5][1], V[5][2]);
  };

  // ---- floors -----------------------------------------------------------------------------
  const neighbourBelow = (p: Pt, top: number): number | null => {
    const s = arch.surfaceAt(p);
    if (s.kind === 'void') return arch.baseY;
    if (s.kind === 'ledge') return s.h >= top - 0.01 ? null : s.h;
    return s.h < top - 0.01 ? s.h : null;
  };
  for (const f of arch.floors) emitFloor(f);
  function emitFloor(f: FloorPiece): void {
    const mat = floorMaterial(f.floor);
    const st = theme.floors[f.floor] ?? Object.values(theme.floors)[0]!;
    // Stairs must read from the high camera even when the risers face away from it: each tread
    // is painted dark at its back (AO under the step above) and light at its front lip (nosing),
    // and alternate treads are nudged light / dark.
    const stepK = f.step ? (Math.round(f.h[0]! / 0.25) % 2 === 0 ? 1.05 : 0.93) : 1;
    const down = f.step ? stairDownhill(f) : null;
    let s0 = 0;
    let s1 = 1;
    if (down) {
      const ss = f.poly.map((q) => q.x * down.x + q.y * down.y);
      s0 = Math.min(...ss);
      s1 = Math.max(...ss);
    }
    emitTop(bucket(f.cluster, 'floor', mat), f.poly, f.h, st.tileYd, (x, y) => {
      const base = mul(bakeFloor(x, y, f.owner), stepK);
      if (!down || s1 - s0 < 1e-3) return base;
      const u = Math.min(1, Math.max(0, (x * down.x + y * down.y - s0) / (s1 - s0)));
      return mul(base, 0.74 + 0.5 * u * u);
    });
    // Exposed sides: stair risers, drops to lower floors, cliffs.
    const pl = fitPlane(f.poly, f.h);
    const bk = bucket(f.cluster, 'stone', stoneMat);
    for (let i = 0; i < f.poly.length; i++) {
      const a = f.poly[i]!;
      const b = f.poly[(i + 1) % f.poly.length]!;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 0.05) continue;
      const mid = { x: (a.x + b.x) / 2 + ((b.y - a.y) / len) * 0.12, y: (a.y + b.y) / 2 - ((b.x - a.x) / len) * 0.12 };
      const ha = planeAt(pl, a.x, a.y);
      const hb = planeAt(pl, b.x, b.y);
      const low = neighbourBelow(mid, Math.min(ha, hb));
      if (low === null) continue;
      const col = f.step ? mul(cliffTint, 0.8) : cliffTint;
      emitSide(bk, a, b, low, ha, low, hb, stoneTile, mul(col, 0.7), col);
    }
  }

  /** Downhill direction (sim, unit) of the stair flight a step piece belongs to. */
  function stairDownhill(f: FloorPiece): Pt | null {
    const c = d.corridors.find((x) => x.id === f.owner);
    if (!c?.rises?.length) return null;
    const cx = f.poly.reduce((a, q) => a + q.x, 0) / f.poly.length;
    const cy = f.poly.reduce((a, q) => a + q.y, 0) / f.poly.length;
    let best = c.rises[0]!;
    let bd = Infinity;
    for (const r of c.rises) {
      const dd = distToSegment({ x: cx, y: cy }, r.start, r.end);
      if (dd < bd) {
        bd = dd;
        best = r;
      }
    }
    const dx = best.end.x - best.start.x;
    const dy = best.end.y - best.start.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6 || best.endElevation === best.startElevation) return null;
    const sgn = best.endElevation < best.startElevation ? 1 : -1;
    return { x: (dx / len) * sgn, y: (dy / len) * sgn };
  }

  // ---- worn paths between doorways (flat strips of another floor pattern) -----------------
  const pathDone = new Map<string, Poly[]>();
  for (const bd of dress.borders) {
    const mat = floorMaterial(bd.floor, true);
    const st = theme.floors[bd.floor] ?? Object.values(theme.floors)[0]!;
    const bk = bucket(bd.cluster, 'path', mat);
    const done = pathDone.get(bd.owner) ?? [];
    for (const poly of bd.polys) {
      const p = signed(poly) < 0 ? [...poly].reverse() : poly;
      emitTop(bk, p, p.map(() => bd.y + 0.011), st.tileYd, (x, y) => mul(bakeFloor(x, y, bd.owner), 0.98), 2);
      done.push(p);
    }
    pathDone.set(bd.owner, done);
  }
  for (const pth of dress.paths) {
    const mat = floorMaterial(pth.floor, true);
    const st = theme.floors[pth.floor] ?? Object.values(theme.floors)[0]!;
    const bk = bucket(pth.cluster, 'path', mat);
    const prev = pathDone.get(pth.owner) ?? [];
    const room = arch.rooms.find((r) => r.id === pth.owner);
    const clip = room ? ccwRoom(room.def.shape.points) : null;
    for (let i = 0; i < pth.pts.length - 1; i++) {
      const a = pth.pts[i]!;
      const b = pth.pts[i + 1]!;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 0.1) continue;
      const nx = (-(b.y - a.y) / len) * (pth.width / 2);
      const ny = ((b.x - a.x) / len) * (pth.width / 2);
      let seg: Poly = [
        { x: a.x - nx, y: a.y - ny },
        { x: b.x - nx, y: b.y - ny },
        { x: b.x + nx, y: b.y + ny },
        { x: a.x + nx, y: a.y + ny },
      ];
      if (signed(seg) < 0) seg.reverse();
      // Round-ish joint at each bend (octagon) so segments meet without notches.
      const joint: Poly = Array.from({ length: 8 }, (_, k) => ({ x: b.x + Math.cos((k / 8) * Math.PI * 2) * (pth.width / 2), y: b.y + Math.sin((k / 8) * Math.PI * 2) * (pth.width / 2) }));
      for (const piece0 of [seg, joint]) {
        let pieces = subtractAll([piece0], prev);
        if (clip) pieces = pieces.map((p) => clipConvex(p, clip)).filter((p) => p.length >= 3);
        for (const p of pieces) {
          emitTop(bk, p, p.map(() => pth.y + 0.012), st.tileYd, (x, y) => {
            // Worn, slightly lighter middle; a pale kerb line along the edges (never a dark band,
            // which reads as a shadow from the high camera).
            const dEdge = Math.min(distToSegment({ x, y }, a, b), Math.hypot(x - b.x, y - b.y));
            const kerb = smooth(pth.width / 2 - 0.9, pth.width / 2 - 0.35, dEdge);
            const base = bakeFloor(x, y, pth.owner);
            return mul(base, 1.06 + 0.16 * kerb);
          }, 1.0);
          prev.push(p);
        }
      }
    }
    pathDone.set(pth.owner, prev);
  }

  // ---- ledges ---------------------------------------------------------------------------
  const poolAt = (p: Pt) => arch.pools.find((pl) => pointInConvex(p, pl.poly));
  for (const l of arch.ledges) emitLedge(l);
  function emitLedge(l: LedgePiece): void {
    const bk = bucket(l.cluster, 'stone', stoneMat);
    emitTop(bucket(l.cluster, 'ledge', ledgeMat), l.poly, l.h, S.ledgeTop.tileYd, (x, y) => {
      const n = 0.86 + 0.18 * vnoise(x * 0.3, y * 0.3);
      const m = smooth(0.62, 0.9, vnoise(x * 0.21 - 7, y * 0.21 + 3)) * 0.6;
      return [ledgeTint[0] * n * (1 + (mossTint[0] - 1) * m), ledgeTint[1] * n * (1 + (mossTint[1] - 1) * m), ledgeTint[2] * n * (1 + (mossTint[2] - 1) * m)];
    }, 3);
    for (let i = 0; i < l.poly.length; i++) {
      const a = l.poly[i]!;
      const b = l.poly[(i + 1) % l.poly.length]!;
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 0.05) continue;
      const out = { x: (b.y - a.y) / len, y: -(b.x - a.x) / len };
      const mid = { x: (a.x + b.x) / 2 + out.x * 0.12, y: (a.y + b.y) / 2 + out.y * 0.12 };
      const ha = planeAt(l.plane, a.x, a.y);
      const hb = planeAt(l.plane, b.x, b.y);
      const s = arch.surfaceAt(mid);
      if (s.kind === 'floor') {
        // The lip facing the walkable floor: serpent frieze (one trim band tall).
        // Down past the stepped floor so no sliver shows on stairs.
        const fa = arch.renderedFloorAt({ x: a.x + out.x * 0.12, y: a.y + out.y * 0.12 }) - 0.2;
        const fb = arch.renderedFloorAt({ x: b.x + out.x * 0.12, y: b.y + out.y * 0.12 }) - 0.2;
        if (trimOn) emitSide(bucket(l.cluster, 'trim', trimMat), a, b, fa, ha, fb, hb, trimTile, [0.95, 0.93, 0.88], [1.05, 1.03, 0.98], 0.25);
        else emitSide(bk, a, b, fa, ha, fb, hb, stoneTile, mul(cliffTint, 0.8), ledgeTint);
      } else if (s.kind === 'pool') {
        const p = poolAt(mid)!;
        emitSide(bk, a, b, p.waterY - 0.2, ha, p.waterY - 0.2, hb, stoneTile, mul(cliffTint, 0.55), cliffTint);
      } else if (s.kind === 'ledge' && s.h < Math.min(ha, hb) - 0.02) {
        emitSide(bk, a, b, s.h, ha, s.h, hb, stoneTile, mul(cliffTint, 0.75), cliffTint);
      } else if (s.kind === 'void') {
        emitSide(bk, a, b, arch.baseY, ha, arch.baseY, hb, stoneTile, mul(cliffTint, 0.5), cliffTint);
      }
    }
  }
  // Pool basins: a dark floor under the water so the basin reads as deep.
  for (const p of arch.pools) {
    const bk = bucket(p.cluster, 'stone', stoneMat);
    emitTop(bk, p.poly, p.poly.map(() => p.waterY - 0.25), stoneTile, () => mul(cliffTint, 0.35), 99);
  }

  // ---- walls + posts -------------------------------------------------------------------
  const TRIM_H = 0.6;
  for (const w of arch.walls) {
    const wallCut = [w.n.x, -w.n.y, w.cutY, CUT_KIND.wall] as const;
    const bk = enclosed ? cutBucket(w.cluster, 'wall', wallCutMat, wallCut) : bucket(w.cluster, 'stone', stoneMat);
    const a2 = { x: w.a.x + w.n.x * w.thick, y: w.a.y + w.n.y * w.thick };
    const b2 = { x: w.b.x + w.n.x * w.thick, y: w.b.y + w.n.y * w.thick };
    const base = w.base;
    const top = w.top;
    const foot = w.foot;
    const lowCol = mul(wallTint, 0.55);
    const mossCol: RGB = [wallTint[0] * mossTint[0] * 0.8, wallTint[1] * mossTint[1] * 0.8, wallTint[2] * mossTint[2] * 0.8];
    const hiCol = mul(wallTint, 1.04);
    const band = trimOn && w.facing === 'far' && !w.corridor && top - foot > 2.5;
    const bandBase = band ? top - TRIM_H - 0.15 : top;
    // Inner face (toward the room): dark below the ledge, moss at the foot, painted light up top.
    emitSide(bk, w.b, w.a, base, foot, base, foot, stoneTile, lowCol, mossCol);
    emitSide(bk, w.b, w.a, foot, foot + 0.9, foot, foot + 0.9, stoneTile, mossCol, wallTint);
    emitSide(bk, w.b, w.a, foot + 0.9, bandBase, foot + 0.9, bandBase, stoneTile, wallTint, hiCol);
    if (band) {
      const tb = enclosed ? cutBucket(w.cluster, 'wall', trimCutMat, wallCut) : bucket(w.cluster, 'trim', trimMat);
      emitSide(tb, w.b, w.a, bandBase, bandBase + TRIM_H, bandBase, bandBase + TRIM_H, trimTile, [0.95, 0.93, 0.88], [1.05, 1.02, 0.95], 0.25);
      emitSide(bk, w.b, w.a, bandBase + TRIM_H, top, bandBase + TRIM_H, top, stoneTile, hiCol, hiCol);
    }
    // Outer face and end caps (seen over near walls from the camera side).
    emitSide(bk, a2, b2, base, top, base, top, stoneTile, mul(wallTint, 0.6), mul(wallTint, 0.85));
    emitSide(bk, w.a, a2, base, top, base, top, stoneTile, mul(wallTint, 0.6), mul(wallTint, 0.9));
    emitSide(bk, b2, w.b, base, top, base, top, stoneTile, mul(wallTint, 0.6), mul(wallTint, 0.9));
    // Cap (lighter painted top).
    emitTop(bk, [w.a, w.b, b2, a2].reverse(), [top, top, top, top], stoneTile, () => mul(wallTint, 1.12), 99);
    // +dungeon2: section cap at the cut height (only seen where the cutaway removed the wall above).
    if (enclosed && w.cutY < top - 0.05) {
      const cb = cutBucket(w.cluster, 'wall', wallCutMat, CUT_NEVER);
      emitTop(cb, [w.a, w.b, b2, a2].reverse(), [w.cutY, w.cutY, w.cutY, w.cutY], stoneTile, () => mul(wallTint, 0.78), 99);
    }
  }
  for (const p of arch.posts) {
    if (p.kind === 'timber') {
      emitBox(cutBucket(p.cluster, 'beam', woodCutMat, [p.n?.x ?? 0, -(p.n?.y ?? 0), p.cutY, CUT_KIND.wall]), p.pos, p.size / 2, p.size / 2, p.base, p.top, 0, lin(0x8a6a48));
      continue;
    }
    const bk = enclosed ? cutBucket(p.cluster, 'wall', wallCutMat, [p.n?.x ?? 0, -(p.n?.y ?? 0), p.cutY, CUT_KIND.wall]) : bucket(p.cluster, 'stone', stoneMat);
    const s = p.size / 2;
    const ring = [
      { x: p.pos.x - s, y: p.pos.y - s },
      { x: p.pos.x + s, y: p.pos.y - s },
      { x: p.pos.x + s, y: p.pos.y + s },
      { x: p.pos.x - s, y: p.pos.y + s },
    ];
    for (let i = 0; i < 4; i++) emitSide(bk, ring[i]!, ring[(i + 1) % 4]!, p.base, p.top - 0.45, p.base, p.top - 0.45, stoneTile, mul(wallTint, 0.6), mul(wallTint, 1.0));
    const c = s + 0.18;
    const cap = [
      { x: p.pos.x - c, y: p.pos.y - c },
      { x: p.pos.x + c, y: p.pos.y - c },
      { x: p.pos.x + c, y: p.pos.y + c },
      { x: p.pos.x - c, y: p.pos.y + c },
    ];
    const capCol = mul(wallTint, p.kind === 'opening' ? 1.18 : 1.1);
    if (p.kind === 'opening') {
      // A jade band under the cap marks doorway posts (the cult's colour, kept small).
      const jb = mul(lin(0x3f8f78), 1.0);
      for (let i = 0; i < 4; i++) emitSide(bk, ring[i]!, ring[(i + 1) % 4]!, p.top - 0.75, p.top - 0.45, p.top - 0.75, p.top - 0.45, stoneTile, jb, jb);
    }
    for (let i = 0; i < 4; i++) emitSide(bk, cap[i]!, cap[(i + 1) % 4]!, p.top - 0.45, p.top, p.top - 0.45, p.top, stoneTile, capCol, capCol);
    emitTop(bk, cap, [p.top, p.top, p.top, p.top], stoneTile, () => mul(capCol, 1.1), 99);
    if (enclosed && p.cutY < p.top - 0.5) emitTop(cutBucket(p.cluster, 'wall', wallCutMat, CUT_NEVER), ring, [p.cutY, p.cutY, p.cutY, p.cutY], stoneTile, () => mul(wallTint, 0.78), 99);
  }
  if (enclosed) emitEnclosed();

  // ---- +dungeon2: enclosed interiors (lintels, ceilings, beams, arcade roofs, garth) --------------
  /** Quad from four three-space points; the winding is fixed so its face points along `want`. */
  function emitQuad(bk: Bucket, P: readonly (readonly [number, number, number])[], want: readonly [number, number, number], uv: readonly (readonly [number, number])[], col: readonly RGB[]): void {
    const [p0, p1, p2] = P as [readonly [number, number, number], readonly [number, number, number], readonly [number, number, number]];
    const ux = p1[0] - p0[0];
    const uy = p1[1] - p0[1];
    const uz = p1[2] - p0[2];
    const vx = p2[0] - p0[0];
    const vy = p2[1] - p0[1];
    const vz = p2[2] - p0[2];
    const cx = uy * vz - uz * vy;
    const cy = uz * vx - ux * vz;
    const cz = ux * vy - uy * vx;
    const flip = cx * want[0] + cy * want[1] + cz * want[2] < 0;
    const order = flip ? [0, 2, 1, 0, 3, 2] : [0, 1, 2, 0, 2, 3];
    const l = Math.hypot(want[0], want[1], want[2]) || 1;
    for (const i of order) {
      const p = P[i]!;
      const c = col[i]!;
      bk.v(p[0], p[1], p[2], want[0] / l, want[1] / l, want[2] / l, uv[i]![0], uv[i]![1], c[0], c[1], c[2]);
    }
  }

  /** Axis-aligned box on a square footprint (posts, timber props). */
  function emitBox(bk: Bucket, c: Pt, hx: number, hy: number, base: number, top: number, _yaw: number, col: RGB): void {
    const ring = [
      { x: c.x - hx, y: c.y - hy },
      { x: c.x + hx, y: c.y - hy },
      { x: c.x + hx, y: c.y + hy },
      { x: c.x - hx, y: c.y + hy },
    ];
    for (let i = 0; i < 4; i++) emitSide(bk, ring[i]!, ring[(i + 1) % 4]!, base, top, base, top, S.wood.tileYd, mul(col, 0.7), col);
    emitTop(bk, ring, [top, top, top, top], S.wood.tileYd, () => mul(col, 1.1), 99);
  }

  /** Oriented beam a→b (sim), `w` wide, bottom at y, `h` tall. */
  function emitBeam(bk: Bucket, a: Pt, b: Pt, y: number, h: number, w: number, col: RGB, tile: number): void {
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < 0.05) return;
    const nx = (-(b.y - a.y) / len) * (w / 2);
    const ny = ((b.x - a.x) / len) * (w / 2);
    const ring = [
      { x: a.x - nx, y: a.y - ny },
      { x: b.x - nx, y: b.y - ny },
      { x: b.x + nx, y: b.y + ny },
      { x: a.x + nx, y: a.y + ny },
    ];
    for (let i = 0; i < 4; i++) emitSide(bk, ring[i]!, ring[(i + 1) % 4]!, y, y + h, y, y + h, tile, mul(col, 0.72), col);
    emitTop(bk, ring, [y + h, y + h, y + h, y + h], tile, () => mul(col, 1.08), 99);
    const P = ring.map((q) => [q.x, y, -q.y] as const);
    emitQuad(bk, P, [0, -1, 0], ring.map((q) => [q.x / tile, q.y / tile] as const), [mul(col, 0.6), mul(col, 0.6), mul(col, 0.6), mul(col, 0.6)]);
  }

  function emitEnclosed(): void {
    const E = theme.arch.enclosed!;
    const ceilTint = mul(wallTint, 0.62);
    const ribTint = mul(wallTint, 0.92);
    const caveTint = mul(cliffTint, 0.8);
    const woodTint = lin(0x8a6a48);
    const roofTint: RGB = mul(lin(0x7aa889), 1.3); // jade glazed tiles (read as roof, not void, from above)

    // Lintels: the room wall carried over each corridor mouth on an arched underside.
    for (const l of arch.lintels) {
      const bk = cutBucket(l.cluster, 'wall', wallCutMat, [l.n.x, -l.n.y, l.foot + E.cutHeight, CUT_KIND.wall]);
      const N = 10;
      const len = Math.hypot(l.b.x - l.a.x, l.b.y - l.a.y);
      const under = (u: number): number => l.spring + (l.apex - l.spring) * Math.sqrt(Math.max(0, 1 - (2 * u - 1) ** 2));
      for (let j = 0; j < N; j++) {
        const u0 = j / N;
        const u1 = (j + 1) / N;
        const p0 = { x: l.a.x + (l.b.x - l.a.x) * u0, y: l.a.y + (l.b.y - l.a.y) * u0 };
        const p1 = { x: l.a.x + (l.b.x - l.a.x) * u1, y: l.a.y + (l.b.y - l.a.y) * u1 };
        const o0 = { x: p0.x + l.n.x * l.thick, y: p0.y + l.n.y * l.thick };
        const o1 = { x: p1.x + l.n.x * l.thick, y: p1.y + l.n.y * l.thick };
        const b0 = under(u0);
        const b1 = under(u1);
        emitSide(bk, p1, p0, b1, l.top, b0, l.top, stoneTile, mul(wallTint, 0.85), mul(wallTint, 1.04));
        emitSide(bk, o0, o1, b0, l.top, b1, l.top, stoneTile, mul(wallTint, 0.6), mul(wallTint, 0.85));
        emitTop(bk, [p0, p1, o1, o0].reverse(), [l.top, l.top, l.top, l.top], stoneTile, () => mul(wallTint, 1.12), 99);
        // End faces (seen where a gable rises above a low wall, e.g. the cloister gates).
        if (j === 0 || j === N - 1) {
          const e = j === 0 ? { p: p0, o: o0, y: b0, s: -1 } : { p: p1, o: o1, y: b1, s: 1 };
          const ux = ((l.b.x - l.a.x) / len) * e.s;
          const uy = ((l.b.y - l.a.y) / len) * e.s;
          const ec = mul(wallTint, 0.8);
          emitQuad(bk, [[e.p.x, e.y, -e.p.y], [e.o.x, e.y, -e.o.y], [e.o.x, l.top, -e.o.y], [e.p.x, l.top, -e.p.y]], [ux, 0, -uy], [[0, e.y / stoneTile], [l.thick / stoneTile, e.y / stoneTile], [l.thick / stoneTile, l.top / stoneTile], [0, l.top / stoneTile]], [ec, ec, ec, ec]);
        }
        const arcCol = mul(ribTint, 0.85);
        emitQuad(bk, [[p0.x, b0, -p0.y], [p1.x, b1, -p1.y], [o1.x, b1, -o1.y], [o0.x, b0, -o0.y]], [0, -1, 0], [[(u0 * len) / stoneTile, 0], [(u1 * len) / stoneTile, 0], [(u1 * len) / stoneTile, l.thick / stoneTile], [(u0 * len) / stoneTile, l.thick / stoneTile]], [arcCol, arcCol, arcCol, arcCol]);
      }
    }

    // Ceilings: barrel vaults (stone, with ribs), cave domes, plank roofs (mine).
    for (const c of arch.ceilings) {
      const L = Math.hypot(c.b.x - c.a.x, c.b.y - c.a.y);
      if (L < 0.05) continue;
      const dir = { x: (c.b.x - c.a.x) / L, y: (c.b.y - c.a.y) / L };
      const nrm = { x: -dir.y, y: dir.x };
      const zCut = Math.min(c.springA, c.springB) - 0.2;
      if (c.kind === 'beams') {
        const bk = cutBucket(c.cluster, 'ceil', woodCutMat, [0, 0, zCut, CUT_KIND.ceiling]);
        const h = (t: number): number => c.springA + (c.springB - c.springA) * (t / L) + 0.7;
        const P = [
          [c.a.x - nrm.x * (c.span / 2), h(0), -(c.a.y - nrm.y * (c.span / 2))],
          [c.b.x - nrm.x * (c.span / 2), h(L), -(c.b.y - nrm.y * (c.span / 2))],
          [c.b.x + nrm.x * (c.span / 2), h(L), -(c.b.y + nrm.y * (c.span / 2))],
          [c.a.x + nrm.x * (c.span / 2), h(0), -(c.a.y + nrm.y * (c.span / 2))],
        ] as const;
        const t = S.wood.tileYd;
        const dk = mul(woodTint, 0.5);
        emitQuad(bk, P, [0, -1, 0], [[0, 0], [L / t, 0], [L / t, c.span / t], [0, c.span / t]], [dk, dk, dk, dk]);
        continue;
      }
      const cave = c.kind === 'cave';
      const rib = c.ribEvery < 0;
      const bk = cutBucket(c.cluster, 'ceil', wallCutMat, [0, 0, zCut, CUT_KIND.ceiling]);
      const M = 12;
      const nA = Math.max(1, Math.ceil(L / 3));
      const spring = (t: number): number => c.springA + (c.springB - c.springA) * (t / L);
      const at = (t: number, th: number): [number, number, number] => {
        const across = -(c.span / 2) * Math.cos(th);
        const x = c.a.x + dir.x * t + nrm.x * across;
        const y = c.a.y + dir.y * t + nrm.y * across;
        let h = spring(t) + c.rise * Math.sin(th);
        if (cave) h += (vnoise(x * 0.35, y * 0.35) - 0.5) * 2.4 * Math.sin(th);
        return [x, h, -y];
      };
      const colAt = (th: number): RGB => {
        const k = 0.75 + 0.35 * (1 - Math.sin(th)); // darker toward the crown (painted AO)
        return mul(cave ? caveTint : rib ? ribTint : ceilTint, rib ? 1 : k);
      };
      const ribTs = c.ribEvery > 0 ? Array.from({ length: Math.max(0, Math.floor(L / c.ribEvery)) }, (_, k) => ((k + 1) * L) / (Math.floor(L / c.ribEvery) + 1)) : [];
      for (let i = 0; i < nA; i++) {
        const t0 = (L * i) / nA;
        const t1 = (L * (i + 1)) / nA;
        for (let j = 0; j < M; j++) {
          const th0 = (Math.PI * j) / M;
          const th1 = (Math.PI * (j + 1)) / M;
          const thm = (th0 + th1) / 2;
          const want = [nrm.x * Math.cos(thm), -Math.sin(thm), -nrm.y * Math.cos(thm)] as const;
          emitQuad(bk, [at(t0, th0), at(t1, th0), at(t1, th1), at(t0, th1)], want, [[t0 / stoneTile, (th0 * c.span) / 2 / stoneTile], [t1 / stoneTile, (th0 * c.span) / 2 / stoneTile], [t1 / stoneTile, (th1 * c.span) / 2 / stoneTile], [t0 / stoneTile, (th1 * c.span) / 2 / stoneTile]], [colAt(th0), colAt(th0), colAt(th1), colAt(th1)]);
        }
      }
      // Transverse ribs across room vaults (a lighter band hanging a little below the vault).
      if (!cave)
        for (const tr of ribTs) {
          const r0 = tr - 0.45;
          const r1 = tr + 0.45;
          for (let j = 0; j < M; j++) {
            const th0 = (Math.PI * j) / M;
            const th1 = (Math.PI * (j + 1)) / M;
            const lo = (p: [number, number, number]): [number, number, number] => [p[0], p[1] - 0.35, p[2]];
            const thm = (th0 + th1) / 2;
            const want = [nrm.x * Math.cos(thm), -Math.sin(thm), -nrm.y * Math.cos(thm)] as const;
            emitQuad(bk, [lo(at(r0, th0)), lo(at(r1, th0)), lo(at(r1, th1)), lo(at(r0, th1))], want, [[0, 0], [0.2, 0], [0.2, 0.2], [0, 0.2]], [ribTint, ribTint, ribTint, ribTint]);
          }
        }
      // Room vaults: close the half-disc gables at both ends.
      if (c.ribEvery > 0)
        for (const [t, s] of [
          [0, 1],
          [L, -1],
        ] as const) {
          const centre: [number, number, number] = [c.a.x + dir.x * t, spring(t), -(c.a.y + dir.y * t)];
          for (let j = 0; j < M; j++) {
            const p0 = at(t, (Math.PI * j) / M);
            const p1 = at(t, (Math.PI * (j + 1)) / M);
            emitQuad(bk, [centre, p0, p1, p1], [dir.x * s, 0, -dir.y * s], [[0, 0], [0.3, 0], [0.3, 0.3], [0.3, 0.3]], [ceilTint, ceilTint, ceilTint, ceilTint]);
          }
        }
    }

    // Beams: mine timber cross beams and the cloister architraves (overheads).
    for (const b of arch.beams) {
      const bk = cutBucket(b.cluster, 'beam', b.wood ? woodCutMat : wallCutMat, [0, 0, b.y - 0.01, CUT_KIND.overhead]);
      emitBeam(bk, b.a, b.b, b.y, b.h, b.w, b.wood ? woodTint : mul(wallTint, 1.05), b.wood ? S.wood.tileYd : stoneTile);
    }

    // Arcade roofs: jade tiles on top (overhead), dark rafters underneath (a ceiling).
    for (const r of arch.roofs) {
      const lo = Math.min(...r.h);
      const top = cutBucket(r.cluster, 'roof', woodCutMat, [0, 0, lo - 1, CUT_KIND.overhead]);
      emitTop(top, r.poly, r.h.map((h) => h + 0.35), S.wood.tileYd, (x, y) => mul(roofTint, 0.85 + 0.25 * vnoise(x * 0.5, y * 0.5)), 3);
      const under = cutBucket(r.cluster, 'ceil', woodCutMat, [0, 0, lo, CUT_KIND.ceiling]);
      const P = r.poly.map((q, i) => [q.x, r.h[i]!, -q.y] as const);
      const dk = mul(woodTint, 0.55);
      emitQuad(under, P, [0, -1, 0], r.poly.map((q) => [q.x / S.wood.tileYd, q.y / S.wood.tileYd] as const), [dk, dk, dk, dk]);
      // Fascia along the low edge so the roof reads as a solid slab from the courtyard.
      for (let i = 0; i < r.poly.length; i++) {
        const j = (i + 1) % r.poly.length;
        if (Math.abs(r.h[i]! - lo) > 0.01 || Math.abs(r.h[j]! - lo) > 0.01) continue;
        emitSide(top, r.poly[i]!, r.poly[j]!, lo - 0.05, lo + 0.4, lo - 0.05, lo + 0.4, S.wood.tileYd, mul(roofTint, 0.6), mul(roofTint, 0.8));
      }
    }

    // Cloister garth: a garden floor pattern inside the colonnade.
    for (const r of arch.rooms) {
      const arc = r.style.arcade;
      if (!arc?.garth || !r.rect) continue;
      const R = r.rect;
      const G = rectPoly(R.x0 + arc.inset + 0.6, R.y0 + arc.inset + 0.6, R.x1 - arc.inset - 0.6, R.y1 - arc.inset - 0.6);
      const mat = floorMaterial(arc.garth, true);
      const st = theme.floors[arc.garth] ?? Object.values(theme.floors)[0]!;
      emitTop(bucket(r.cluster, 'path', mat), G, G.map(() => r.elevation + 0.013), st.tileYd, (x, y) => mul(bakeFloor(x, y, r.id), 1.02), 2);
    }
  }

  // ---- arch meshes ---------------------------------------------------------------------
  for (const e of buckets.values()) {
    if (e.b.count === 0) continue;
    const geo = e.b.geometry();
    owned.push(geo);
    const m = new THREE.Mesh(geo, e.mat);
    m.name = `${e.kind}:${e.cluster}`;
    m.receiveShadow = true;
    // +dungeon2: ceilings, beams and roofs never cast (an indoor key light would black out rooms).
    m.castShadow = e.kind === 'stone' || e.kind === 'ledge' || e.kind === 'wall';
    m.userData['noStyle'] = true;
    group.add(m);
    drawables++;
  }

  // ---- props: one merged mesh per cluster (+ detail), all on the prop atlas ---------------
  const atlas = assets.propAtlas();
  const propMat = ownedToonMaterial({ color: 0xffffff, map: atlas, vertexColors: true });
  propMat.name = 'props';
  owned.push(propMat);
  const procCache = new Map<string, THREE.BufferGeometry | null>();
  owned.push({ dispose: () => procCache.forEach((g) => g?.dispose()) });
  const sourceGeo = (asset: string): THREE.BufferGeometry | null => {
    // +field: 'out:*' are the outdoor procs (outdoor/procs.ts: rune pillars, roots, crystals...).
    if (asset.startsWith('proc:') || asset.startsWith('out:')) {
      const out = asset.startsWith('out:');
      const key = out ? asset : asset.slice(5);
      const had = procCache.has(key);
      const g = out ? outdoorProc(asset.slice(4), procCache, key) : venomProc(key, procCache);
      void had;
      if (g && !g.attributes['uv']) {
        const n = (g.attributes['position'] as THREE.BufferAttribute).count;
        const uv = new Float32Array(n * 2);
        for (let i = 0; i < n; i++) {
          uv[i * 2] = ATLAS_WHITE[0];
          uv[i * 2 + 1] = ATLAS_WHITE[1];
        }
        g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
        addOutlineNormals(g);
      }
      return g;
    }
    return assets.kitGeometry(asset);
  };
  const propGroups = new Map<string, Placement[]>();
  for (const p of dress.placements) {
    const key = `${p.cluster}|${p.layer === 'detail' ? 'detail' : 'core'}`;
    const l = propGroups.get(key);
    if (l) l.push(p);
    else propGroups.set(key, [p]);
  }
  let propCount = 0;
  const M4 = new THREE.Matrix4();
  const N3 = new THREE.Matrix3();
  const Qy = new THREE.Quaternion();
  const Qx = new THREE.Quaternion();
  const V = new THREE.Vector3();
  const SC = new THREE.Vector3();
  const AX = new THREE.Vector3(1, 0, 0);
  const AY = new THREE.Vector3(0, 1, 0);
  for (const [key, list] of propGroups) {
    const isDetail = key.endsWith('detail');
    let total = 0;
    const srcs = list.map((p) => sourceGeo(p.asset));
    srcs.forEach((g) => (total += g ? (g.attributes['position'] as THREE.BufferAttribute).count : 0));
    if (total === 0) continue;
    const pos = new Float32Array(total * 3);
    const nrm = new Float32Array(total * 3);
    const onr = new Float32Array(total * 3);
    const uvs = new Float32Array(total * 2);
    const col = new Float32Array(total * 3);
    let o = 0;
    list.forEach((p, idx) => {
      const g = srcs[idx];
      if (!g) return;
      propCount++;
      Qy.setFromAxisAngle(AY, p.yaw);
      if (p.tilt) Qy.multiply(Qx.setFromAxisAngle(AX, p.tilt));
      const st = p.stretch ?? [1, 1, 1];
      M4.compose(V.set(p.pos.x, p.y, -p.pos.y), Qy, SC.set(p.scale * st[0], p.scale * st[1], p.scale * st[2]));
      N3.getNormalMatrix(M4);
      // Direct typed-array access (kit / proc geometries are non-indexed Float32 arrays).
      const P = (g.attributes['position'] as THREE.BufferAttribute).array as Float32Array;
      const Nn = (g.attributes['normal'] as THREE.BufferAttribute).array as Float32Array;
      const On = ((g.attributes['outlineNormal'] as THREE.BufferAttribute | undefined)?.array as Float32Array | undefined) ?? Nn;
      const Uv = (g.attributes['uv'] as THREE.BufferAttribute | undefined)?.array as Float32Array | undefined;
      const C = (g.attributes['color'] as THREE.BufferAttribute | undefined)?.array as Float32Array | undefined;
      const e = M4.elements;
      const ne = N3.elements;
      const count = P.length / 3;
      const [tr, tg, tb] = p.tint;
      for (let i = 0; i < count; i++, o++) {
        const i3 = i * 3;
        const o3 = o * 3;
        const x = P[i3]!;
        const y = P[i3 + 1]!;
        const z = P[i3 + 2]!;
        pos[o3] = e[0]! * x + e[4]! * y + e[8]! * z + e[12]!;
        pos[o3 + 1] = e[1]! * x + e[5]! * y + e[9]! * z + e[13]!;
        pos[o3 + 2] = e[2]! * x + e[6]! * y + e[10]! * z + e[14]!;
        let a = Nn[i3]!;
        let b = Nn[i3 + 1]!;
        let c = Nn[i3 + 2]!;
        let nx = ne[0]! * a + ne[3]! * b + ne[6]! * c;
        let ny = ne[1]! * a + ne[4]! * b + ne[7]! * c;
        let nz = ne[2]! * a + ne[5]! * b + ne[8]! * c;
        let l = 1 / (Math.hypot(nx, ny, nz) || 1);
        nrm[o3] = nx * l;
        nrm[o3 + 1] = ny * l;
        nrm[o3 + 2] = nz * l;
        a = On[i3]!;
        b = On[i3 + 1]!;
        c = On[i3 + 2]!;
        nx = ne[0]! * a + ne[3]! * b + ne[6]! * c;
        ny = ne[1]! * a + ne[4]! * b + ne[7]! * c;
        nz = ne[2]! * a + ne[5]! * b + ne[8]! * c;
        l = 1 / (Math.hypot(nx, ny, nz) || 1);
        onr[o3] = nx * l;
        onr[o3 + 1] = ny * l;
        onr[o3 + 2] = nz * l;
        uvs[o * 2] = Uv ? Uv[i * 2]! : ATLAS_WHITE[0];
        uvs[o * 2 + 1] = Uv ? Uv[i * 2 + 1]! : ATLAS_WHITE[1];
        col[o3] = (C ? C[i3]! : 1) * tr;
        col[o3 + 1] = (C ? C[i3 + 1]! : 1) * tg;
        col[o3 + 2] = (C ? C[i3 + 2]! : 1) * tb;
      }
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos.subarray(0, o * 3), 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(nrm.subarray(0, o * 3), 3));
    geo.setAttribute('outlineNormal', new THREE.BufferAttribute(onr.subarray(0, o * 3), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uvs.subarray(0, o * 2), 2));
    geo.setAttribute('color', new THREE.BufferAttribute(col.subarray(0, o * 3), 3));
    geo.computeBoundingSphere();
    owned.push(geo);
    const mesh = new THREE.Mesh(geo, propMat);
    mesh.name = `props:${key}`;
    mesh.castShadow = !isDetail;
    mesh.receiveShadow = true;
    mesh.userData['noStyle'] = true;
    group.add(mesh);
    drawables++;
    if (!isDetail) {
      addHull(mesh, propMat, hulls, 'prop');
      drawables++;
    } else detail.push(mesh);
  }

  // ---- flames -----------------------------------------------------------------------------
  if (dress.flames.length) {
    const fGeo = new THREE.PlaneGeometry(1, 1).translate(0, 0.5, 0);
    const fMat = flameMaterial();
    owned.push(fGeo, fMat);
    const fm = new THREE.InstancedMesh(fGeo, fMat, dress.flames.length);
    const Q0 = new THREE.Quaternion();
    dress.flames.forEach((f, i) => fm.setMatrixAt(i, new THREE.Matrix4().compose(new THREE.Vector3(f.pos.x, f.y, -f.pos.y), Q0, new THREE.Vector3(f.w, f.h, 1))));
    fm.computeBoundingSphere();
    fm.frustumCulled = false;
    group.add(fm);
    drawables++;
  }

  // ---- fake light pools (warm braziers / torches, cool jade by pools and statues) ---------
  if (dress.glows.length) {
    const pos: number[] = [];
    const uv: number[] = [];
    const glow: number[] = [];
    const seed: number[] = [];
    const idx: number[] = [];
    dress.glows.forEach((g, i) => {
      const y = arch.surfaceAt(g.pos).h + 0.03;
      const c = g.temp === 'jade' ? glowJade : glowWarm;
      const base = pos.length / 3;
      for (const [sx, sy] of [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ] as const) {
        pos.push(g.pos.x + sx * g.r, y, -(g.pos.y + sy * g.r));
        uv.push((sx + 1) / 2, (sy + 1) / 2);
        glow.push(c[0], c[1], c[2], g.strength);
        seed.push((i * 1.7) % 6);
      }
      idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setAttribute('aGlow', new THREE.Float32BufferAttribute(glow, 4));
    geo.setAttribute('aSeed', new THREE.Float32BufferAttribute(seed, 1));
    geo.setIndex(idx);
    geo.computeBoundingSphere();
    const mat = lightPoolMaterial();
    owned.push(geo, mat);
    const m = new THREE.Mesh(geo, mat);
    m.renderOrder = 1;
    m.frustumCulled = false;
    group.add(m);
    drawables++;
  }

  // ---- poison water + spout streams ---------------------------------------------------------
  if (arch.pools.length) {
    const pos: number[] = [];
    const uv: number[] = [];
    const edge: number[] = [];
    const stream: number[] = [];
    const idx: number[] = [];
    for (const p of arch.pools) {
      const bb = bbox(p.poly);
      const inset = 0.7;
      const xs = [bb.x0, bb.x0 + inset, bb.x1 - inset, bb.x1];
      const ys = [bb.y0, bb.y0 + inset, bb.y1 - inset, bb.y1];
      const base = pos.length / 3;
      for (let j = 0; j < 4; j++)
        for (let i = 0; i < 4; i++) {
          pos.push(xs[i]!, p.waterY, -ys[j]!);
          uv.push(i / 3, j / 3);
          edge.push(i === 0 || i === 3 || j === 0 || j === 3 ? 0 : 1);
          stream.push(0);
        }
      for (let j = 0; j < 3; j++)
        for (let i = 0; i < 3; i++) {
          const a = base + j * 4 + i;
          idx.push(a, a + 1, a + 5, a, a + 5, a + 4);
        }
      // Stream from the spout (back wall side of the basin) down to the water.
      const fx = Math.cos(p.yaw);
      const fy = Math.sin(p.yaw);
      const back = { x: p.center.x - fx * (p.half[1] - 0.2), y: p.center.y - fy * (p.half[1] - 0.2) };
      const top = p.rimY + 0.75;
      const w = 0.32;
      const sb = pos.length / 3;
      for (const [s, y, v] of [
        [-1, p.waterY, 0],
        [1, p.waterY, 0],
        [1, top, 1],
        [-1, top, 1],
      ] as const) {
        pos.push(back.x - fy * w * s, y, -(back.y + fx * w * s));
        uv.push((s + 1) / 2, v);
        edge.push(1);
        stream.push(1);
      }
      idx.push(sb, sb + 1, sb + 2, sb, sb + 2, sb + 3);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setAttribute('aEdge', new THREE.Float32BufferAttribute(edge, 1));
    geo.setAttribute('aStream', new THREE.Float32BufferAttribute(stream, 1));
    geo.setIndex(idx);
    geo.computeBoundingSphere();
    const mat = poisonWaterMaterial(S.water ? texFor(S.water) : null);
    owned.push(geo, mat);
    const m = new THREE.Mesh(geo, mat);
    m.name = 'water';
    group.add(m);
    drawables++;
  }

  // ---- light shafts ---------------------------------------------------------------------------
  if (dress.shafts.length) {
    const pos: number[] = [];
    const uv: number[] = [];
    const idx: number[] = [];
    const tilt = 0.32;
    for (const s of dress.shafts) {
      for (const rot of [0, Math.PI / 2]) {
        const yaw = s.yaw + rot;
        const ax = Math.cos(yaw);
        const az = Math.sin(yaw);
        const base = pos.length / 3;
        for (const [u, v] of [
          [0, 0],
          [1, 0],
          [1, 1],
          [0, 1],
        ] as const) {
          const off = (u - 0.5) * s.w;
          const hy = v * s.h;
          // Lean the shaft so it comes in from above the far wall.
          pos.push(s.pos.x + ax * off - Math.sin(tilt) * hy * 0.6, s.y + hy * Math.cos(tilt), -(s.pos.y + az * off + Math.sin(tilt) * hy));
          uv.push(u, v);
        }
        idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.computeBoundingSphere();
    const mat = shaftMaterial();
    owned.push(geo, mat);
    const m = new THREE.Mesh(geo, mat);
    m.renderOrder = 8;
    group.add(m);
    detail.push(m);
    drawables++;
  }

  // ---- floor decals (moss, cracks, puddles, roots) + mosaics ---------------------------------
  const decalTex = floorDecalTexture();
  if (decalTex && dress.decals.length) {
    const pos: number[] = [];
    const uv: number[] = [];
    const col: number[] = [];
    const nrm: number[] = [];
    const idx: number[] = [];
    for (const dc of dress.decals) {
      const cell = DECAL_CELL[DECAL_KINDS.indexOf(dc.kind)]!;
      const c = Math.cos(dc.yaw);
      const s = Math.sin(dc.yaw);
      const base = pos.length / 3;
      for (const [u, v] of [
        [0, 0],
        [1, 0],
        [1, 1],
        [0, 1],
      ] as const) {
        const lx = (u - 0.5) * 2 * dc.r;
        const ly = (v - 0.5) * 2 * dc.r;
        pos.push(dc.pos.x + lx * c - ly * s, dc.y + 0.015, -(dc.pos.y + lx * s + ly * c));
        uv.push(cell[0] + u * 0.5, cell[1] + v * 0.5);
        col.push(1, 1, 1, dc.alpha);
        nrm.push(0, 1, 0);
      }
      idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 4));
    geo.setIndex(idx);
    geo.computeBoundingSphere();
    const mat = ownedToonMaterial({ map: decalTex, vertexColors: true, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    owned.push(geo, mat, decalTex);
    const m = new THREE.Mesh(geo, mat);
    m.renderOrder = 1;
    m.receiveShadow = true;
    m.userData['noStyle'] = true;
    group.add(m);
    detail.push(m);
    drawables++;
  }
  // +field: the barrow's floor decals are glowing rune circles instead of the serpent mosaic.
  if (theme.id === 'hollow_barrow' && dress.mosaics.length) {
    const plane = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
    const mat = groundGlowMaterial([0.22, 0.85, 0.8], 0.42, true);
    owned.push(plane, mat);
    for (const mo of dress.mosaics) {
      const m = new THREE.Mesh(plane, mat);
      m.position.set(mo.pos.x, mo.y + 0.03, -mo.pos.y);
      m.scale.setScalar(mo.r);
      m.renderOrder = 2;
      group.add(m);
      drawables++;
    }
  }
  const mosaicTex = theme.id === 'hollow_barrow' ? null : assets.texture('venom_decal_mosaic');
  if (mosaicTex && dress.mosaics.length) {
    const geos = dress.mosaics.map((mo) => new THREE.PlaneGeometry(mo.r * 2, mo.r * 2).rotateX(-Math.PI / 2).translate(mo.pos.x, mo.y + 0.025, -mo.pos.y));
    const geo = geos.length === 1 ? geos[0]! : mergeAll(geos);
    const mat = ownedToonMaterial({ map: mosaicTex, transparent: true, depthWrite: false, color: 0xe6e6e0, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    owned.push(geo, mat);
    const m = new THREE.Mesh(geo, mat);
    m.renderOrder = 1;
    m.receiveShadow = true;
    m.userData['noStyle'] = true;
    group.add(m);
    drawables++;
  }

  // ---- backdrop (unlit void below everything) + entrance rune --------------------------------
  const b = d.bounds;
  const backGeo = new THREE.PlaneGeometry(b.w + 200, b.h + 200).rotateX(-Math.PI / 2);
  const backMat = new THREE.MeshBasicMaterial({ color: theme.lighting.background });
  owned.push(backGeo, backMat);
  const back = new THREE.Mesh(backGeo, backMat);
  back.position.set(b.x + b.w / 2, arch.baseY + 0.05, -(b.y + b.h / 2));
  group.add(back);
  drawables++;
  const entGeo = new THREE.RingGeometry(1.3, 1.6, 40).rotateX(-Math.PI / 2);
  const entMat = new THREE.MeshBasicMaterial({ color: 0x6f8296, transparent: true, opacity: 0.16, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
  owned.push(entGeo, entMat);
  const ent = new THREE.Mesh(entGeo, entMat);
  ent.position.set(d.entrance.x, arch.floorAt(d.entrance) + 0.02, -d.entrance.y);
  group.add(ent);
  drawables++;

  const ms = typeof performance !== 'undefined' ? performance.now() - t0 : 0;
  if (typeof console !== 'undefined' && ms > 0) console.info(`[toon] ${theme.id} dressing built in ${ms.toFixed(0)} ms: ${arch.floors.length} floor / ${arch.ledges.length} ledge / ${arch.walls.length} wall pieces, ${propCount} props, ${dress.flames.length} flames`);

  return {
    group,
    floorMaterials: [...floorMats.values()],
    detail,
    arch,
    counts: { walls: arch.walls.length, props: propCount, torches: dress.flames.length, drawables, doors: 0, clusters: arch.clusterCount },
    dispose() {
      group.traverse((o) => {
        if (o.userData['hull']) {
          hulls.prop.delete(o);
          hulls.unit.delete(o);
        }
        if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
      });
      group.removeFromParent();
      for (const o of owned) o.dispose();
    },
  };
}

const signed = (p: readonly Pt[]): number => {
  let s = 0;
  for (let i = 0, j = p.length - 1; i < p.length; j = i++) s += p[j]!.x * p[i]!.y - p[i]!.x * p[j]!.y;
  return s / 2;
};
const ccwRoom = (p: readonly Pt[]): Poly => (signed(p) < 0 ? [...p].reverse() : [...p]);

/**
 * Anti-tiling for big floors: a second sample of the same texture, rotated 90° and shifted by
 * whole tiles, blended in by low-frequency world noise, plus a gentle value wobble. Breaks the
 * visible repeat from the high camera at the cost of one extra texture fetch.
 */
function antiTile(m: THREE.MeshToonMaterial): void {
  const base = m.onBeforeCompile;
  m.onBeforeCompile = (sh, r) => {
    base.call(m, sh, r);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>\n${NOISE_GLSL}`)
      .replace(
        '#include <map_fragment>',
        `#ifdef USE_MAP
          vec2 atUv = vMapUv;
          vec4 atA = texture2D(map, atUv);
          vec4 atB = texture2D(map, vec2(-atUv.y, atUv.x) + vec2(0.5, 0.25));
          float atN = noise(atUv * 0.45 + 17.0);
          vec4 sampledDiffuseColor = mix(atA, atB, smoothstep(0.42, 0.58, atN));
          sampledDiffuseColor.rgb *= 0.9 + 0.2 * noise(atUv * 0.21 - 3.0);
          diffuseColor *= sampledDiffuseColor;
        #endif`,
      );
  };
  m.customProgramCacheKey = () => 'toon-antitile';
}

function mergeAll(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
  // Small helper (plane geometries share attributes and are indexed).
  const pos: number[] = [];
  const nrm: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  for (const g of geos) {
    const base = pos.length / 3;
    pos.push(...(g.attributes['position']!.array as Float32Array));
    nrm.push(...(g.attributes['normal']!.array as Float32Array));
    uv.push(...(g.attributes['uv']!.array as Float32Array));
    for (const i of g.index!.array as ArrayLike<number> as number[]) idx.push(base + i);
    g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  out.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  out.setIndex(idx);
  out.computeBoundingSphere();
  return out;
}

export type { ArchLayout, DressLayout };
